<script setup lang="ts">
/**
 * AvatarCropper — 头像裁剪器 (原图铺底 + 居中裁切框)。
 *
 * 交互:
 *   - 画布完整展示原图 (按比例缩放到适应容器), 不裁剪;
 *   - 正方形裁切框固定居中 (头像最终按圆形显示), 框内原亮度, 框外半透明灰罩;
 *   - 拖动图片平移 / 滚轮或滑块缩放, 图片始终覆盖裁切框 (不会露白边);
 *   - 确认后只输出裁切框内的区域 (默认 512×512 PNG)。
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'

const props = defineProps<{
  open: boolean
  file: File | null
  outputSize?: number
}>()

const emit = defineEmits<{
  (e: 'confirm', blob: Blob): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const MAX_W = 460        // 画布最大宽度 (px)
const CROP_RATIO = 0.72  // 裁切框边长 / 画布短边
const OUT = computed(() => props.outputSize ?? 512)

const wrapEl = ref<HTMLDivElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)
const imgEl = ref<HTMLImageElement | null>(null)
const loading = ref(false)
const errorMsg = ref('')

const scale = ref(1)      // 用户缩放倍率 (相对初始 cover)
const offsetX = ref(0)
const offsetY = ref(0)
const dragging = ref(false)
let dragStartX = 0, dragStartY = 0, dragOffX = 0, dragOffY = 0
let objectUrl: string | null = null

/** 画布尺寸 (按图片比例自适应容器宽度) */
const cw = ref(MAX_W)
const ch = ref(MAX_W)
const imgW = ref(0)
const imgH = ref(0)

/** 裁切框 (居中正方形) */
const cropSize = computed(() => Math.min(cw.value, ch.value) * CROP_RATIO)
const cropX = computed(() => (cw.value - cropSize.value) / 2)
const cropY = computed(() => (ch.value - cropSize.value) / 2)

/** 初始 cover 缩放: 图片铺满裁切框所需的最小倍率 */
const baseScale = computed(() => {
  if (!imgW.value || !imgH.value || !cropSize.value) return 1
  return Math.max(cropSize.value / imgW.value, cropSize.value / imgH.value)
})

function draw(): void {
  const cv = canvasEl.value
  const img = imgEl.value
  if (!cv || !img || !imgW.value) return

  // 关键 (v3.13.93 fix): canvas 内在尺寸必须由 JS 直接设置。
  // 此前用 Vue 的 :width/:height 绑定 — Vue 更新这两个 DOM 属性时
  // 会按规范清空画布, 把紧接着 draw() 画的内容整个抹掉 (表现为永远空白)。
  // 现在: 内在尺寸 = 逻辑尺寸 × dpr (高清), 显示尺寸走 CSS。
  const dpr = window.devicePixelRatio || 1
  const pw = Math.round(cw.value * dpr)
  const ph = Math.round(ch.value * dpr)
  if (cv.width !== pw || cv.height !== ph) {
    cv.width = pw
    cv.height = ph
  }
  const ctx = cv.getContext('2d')
  if (!ctx) return
  // setTransform 而非 scale, 避免多次调用累积缩放。
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cw.value, ch.value)

  // 1) 原图完整铺底 (不被裁切, 只做整体缩放/平移)
  const s = baseScale.value * scale.value
  ctx.drawImage(img, offsetX.value, offsetY.value, imgW.value * s, imgH.value * s)

  // 2) 裁切框外灰罩 — 必须在图片之后画, 否则会被图片盖住。
  //    evenodd: 外框(顺) - 内框(顺) = 框外环形区域。
  const cx = cropX.value, cy = cropY.value, cs = cropSize.value
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, cw.value, ch.value)
  ctx.rect(cx, cy, cs, cs)   // 同向子路径, evenodd 形成挖空
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.fill('evenodd')
  ctx.restore()

  // 3) 裁切框虚线 + 实线内描边
  ctx.save()
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.lineWidth = 1.5
  ctx.setLineDash([7, 5])
  ctx.strokeRect(cx + 0.75, cy + 0.75, cs - 1.5, cs - 1.5)
  ctx.restore()

  // 4) 四角实线角标 (常见裁剪器样式)
  ctx.save()
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 3
  ctx.setLineDash([])
  const arm = Math.min(24, cs * 0.16)
  const corners: [number, number, number, number][] = [
    [cx, cy, 1, 1], [cx + cs, cy, -1, 1],
    [cx, cy + cs, 1, -1], [cx + cs, cy + cs, -1, -1],
  ]
  for (const [px, py, sx, sy] of corners) {
    ctx.beginPath()
    ctx.moveTo(px + sx * arm, py)
    ctx.lineTo(px, py)
    ctx.lineTo(px, py + sy * arm)
    ctx.stroke()
  }
  ctx.restore()
}

/** 约束平移: 图片始终完整覆盖裁切框 */
function clampOffsets(): void {
  if (!imgW.value) return
  const s = baseScale.value * scale.value
  const w = imgW.value * s
  const h = imgH.value * s
  const minX = cropX.value + cropSize.value - w
  const minY = cropY.value + cropSize.value - h
  offsetX.value = Math.min(cropX.value, Math.max(minX, offsetX.value))
  offsetY.value = Math.min(cropY.value, Math.max(minY, offsetY.value))
}

/** 缩放时以裁切框中心为锚点, 保持框内构图稳定 */
function setScale(next: number): void {
  const prev = scale.value
  const clamped = Math.min(6, Math.max(1, next))
  if (clamped === prev) return
  const k = clamped / prev
  const cxx = cropX.value + cropSize.value / 2
  const cyy = cropY.value + cropSize.value / 2
  offsetX.value = cxx - (cxx - offsetX.value) * k
  offsetY.value = cyy - (cyy - offsetY.value) * k
  scale.value = clamped
  clampOffsets()
  draw()
}

function reset(): void {
  scale.value = 1
  errorMsg.value = ''
  clampOffsets()
  draw()
}

/** 按容器宽度与图片比例确定画布尺寸 (Modal 首帧 clientWidth 可能还是 0)。 */
function sizeCanvas(): boolean {
  const img = imgEl.value
  if (!img || !imgW.value || !imgH.value) return false
  const avail = Math.max(240, Math.min(MAX_W, wrapEl.value?.clientWidth || MAX_W))
  cw.value = avail
  // 竖图给更多高度, 横图别太高; 上下限保证裁切框有足够操作空间。
  const ratio = imgH.value / imgW.value
  ch.value = Math.max(avail * 0.7, Math.min(avail * 1.4, avail * ratio))
  return cw.value > 0 && ch.value > 0
}

function centerImage(): void {
  const s = baseScale.value
  offsetX.value = (cw.value - imgW.value * s) / 2
  offsetY.value = (ch.value - imgH.value * s) / 2
  clampOffsets()
}

async function loadImage(file: File): Promise<void> {
  if (objectUrl) URL.revokeObjectURL(objectUrl)
  objectUrl = URL.createObjectURL(file)
  const img = new Image()
  // 关键: onload 是异步的, 此刻 Modal 内的 canvas 可能还没完成渲染/布局。
  // 必须 nextTick + rAF 之后再测量与绘制, 否则画布尺寸为 0 → 画不出东西。
  img.onload = async () => {
    imgEl.value = img
    imgW.value = img.naturalWidth || img.width
    imgH.value = img.naturalHeight || img.height
    if (!imgW.value || !imgH.value) {
      loading.value = false
      errorMsg.value = t('settings.auth.cropLoadFailed')
      return
    }
    await nextTick()
    requestAnimationFrame(() => {
      sizeCanvas()
      scale.value = 1
      centerImage()
      draw()
      loading.value = false
    })
  }
  img.onerror = () => {
    loading.value = false
    errorMsg.value = t('settings.auth.cropLoadFailed')
  }
  img.src = objectUrl
  // 兜底: 某些浏览器缓存命中时 onload 极快, 等一帧再核一次。
  requestAnimationFrame(() => {
    if (img.complete && img.naturalWidth > 0 && imgEl.value !== img) {
      void img.onload?.(new Event('load'))
    }
  })
}

function onPointerDown(e: PointerEvent): void {
  dragging.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  dragOffX = offsetX.value
  dragOffY = offsetY.value
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
}
function onPointerMove(e: PointerEvent): void {
  if (!dragging.value) return
  offsetX.value = dragOffX + (e.clientX - dragStartX)
  offsetY.value = dragOffY + (e.clientY - dragStartY)
  clampOffsets()
  draw()
}
function onPointerUp(e: PointerEvent): void {
  dragging.value = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
}
function onWheel(e: WheelEvent): void {
  e.preventDefault()
  setScale(scale.value * (e.deltaY < 0 ? 1.08 : 1 / 1.08))
}
function onZoomInput(e: Event): void {
  setScale(Number((e.target as HTMLInputElement).value))
}

async function confirm(): Promise<void> {
  const img = imgEl.value
  if (!img) return
  const out = document.createElement('canvas')
  out.width = OUT.value
  out.height = OUT.value
  const ctx = out.getContext('2d')
  if (!ctx) return

  // v3.13.94 fix: 必须用 9 参数 drawImage (源矩形 + 目标矩形)。
  // 此前的 5 参数版本被解释为 drawImage(img, dx, dy, dWidth, dHeight) —
  // 即"把整张图画到输出画布的偏移位置", 从未真正裁剪, 结果与预览天壤之别。
  //
  // 换算链: 画布坐标 --(÷s)--> 图片原始像素坐标 (s = 当前绘制缩放倍数)
  const s = baseScale.value * scale.value
  const sx = (cropX.value - offsetX.value) / s
  const sy = (cropY.value - offsetY.value) / s
  const sw = cropSize.value / s
  const sh = cropSize.value / s
  if (sw <= 0 || sh <= 0) return
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT.value, OUT.value)

  const blob = await new Promise<Blob | null>((r) => out.toBlob(r, 'image/png'))
  if (blob) emit('confirm', blob)
}

watch(
  () => props.file,
  (f) => {
    if (f) {
      loading.value = true
      errorMsg.value = ''
      imgEl.value = null
      void loadImage(f)
    } else {
      imgEl.value = null
      imgW.value = 0
    }
  },
  { immediate: true },
)

function onResize(): void {
  if (!imgEl.value) return
  sizeCanvas()
  clampOffsets()
  draw()
}

onMounted(() => {
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('resize', onResize)
  if (objectUrl) URL.revokeObjectURL(objectUrl)
})
</script>

<template>
  <Modal
    :model-value="open"
    :title="t('settings.auth.cropTitle')"
    :close-on-escape="true"
    @close="emit('cancel')"
  >
    <div class="space-y-4">
      <div ref="wrapEl" class="mx-auto max-w-full">
        <canvas
          ref="canvasEl"
          class="max-w-full touch-none rounded-lg border border-border bg-bg-base"
          :style="{ width: `${cw}px`, height: `${ch}px` }"
          :class="[loading ? 'opacity-40' : '', dragging ? 'cursor-grabbing' : 'cursor-grab']"
          @pointerdown="onPointerDown"
          @pointerup="onPointerUp"
          @wheel="onWheel"
        />
      </div>

      <p v-if="errorMsg" class="text-center text-xs text-danger">{{ errorMsg }}</p>
      <p v-else class="text-center text-xs text-fg-muted">
        {{ t('settings.auth.cropHint') }}
      </p>

      <div class="flex items-center gap-3">
        <span class="shrink-0 text-xs text-fg-muted">{{ t('settings.auth.cropZoom') }}</span>
        <input
          type="range"
          min="1"
          max="6"
          step="0.01"
          :value="scale"
          class="flex-1"
          @input="onZoomInput"
        />
      </div>

      <div class="flex justify-end gap-2">
        <Button variant="outline" size="md" @click="emit('cancel')">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="primary" size="md" :disabled="loading || !!errorMsg" @click="confirm">
          {{ t('settings.auth.cropConfirm') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
